import type { BlockField, BlockFieldStyle } from '@/blocks/domain/types';

/** The element styles that print text, so the Style panel's italic / uppercase switches mean something. */
const TEXT_STYLES: ReadonlySet<BlockFieldStyle> = new Set<BlockFieldStyle>([
  'title',
  'heading',
  'subheading',
  'value',
  'text',
  'label_value',
  'caption',
  'signature_line',
  'rich_text'
]);

export interface FieldTextStyle {
  italic: boolean;
  uppercase: boolean;
}

/** Whether the element's style prints text, i.e. offers the Style panel's switches. */
export function fieldStyleHasTextStyle(style: BlockFieldStyle): boolean {
  return TEXT_STYLES.has(style);
}

/**
 * Whether selecting the element swaps it for the in-place text box on the canvas. A `rich_text` element does not: its value is HTML,
 * edited with the rich editor in its settings, and a text box would show the markup. Image / Logo / Dataset hold no text to type.
 */
export function fieldStyleEditsInPlaceAsText(style: BlockFieldStyle): boolean {
  return TEXT_STYLES.has(style) && style !== 'rich_text';
}

/**
 * What the element's Style panel asks for, or `null` when it asks for nothing (the common case: the
 * element then renders exactly as it did before the switches existed). The ONE rule both renderers
 * (`FieldElementRender`, `FieldElementPdf`) read, so HTML and PDF cannot disagree.
 */
export function fieldTextStyleOf(field: Pick<BlockField, 'style' | 'italic' | 'uppercase'>): FieldTextStyle | null {
  if (!TEXT_STYLES.has(field.style)) return null;
  const italic = field.italic === true;
  const uppercase = field.uppercase === true;
  return italic || uppercase ? { italic, uppercase } : null;
}
